import type { Model } from "@/schemas/model.schema";
import type { Pricing, PricingRule, RuleName } from "@/schemas/pricing.schema";
import type { UsageRequestItem } from "@/schemas/usage_requests.schema";
import { PRICE_BLEND_WEIGHTS } from "@/config/model-ratings.config";
import { useOrganizationPricingQuery } from "@/queries/pricing.query";
import { pricingRuleSchema } from "@/schemas/pricing.schema";
import { $t } from "@/services/i18n";

export function isTtsPricingRule(rule: PricingRule): boolean {
  return rule.name === "tts_char" || rule.name === "tts_byte";
}

/**
 * Gets pricing rules for a specific model from the organization pricing
 * @param modelName - The name of the model
 * @param pricing - Optional pricing data (if not provided, will use query)
 * @returns Array of pricing rules for the model, or empty array if not found
 */
export function getModelPricingRules(modelName: string, pricing?: Pricing): PricingRule[] {
  if (!pricing) {
    const pricingQuery = useOrganizationPricingQuery();
    pricing = pricingQuery.data.value ?? undefined;
  }

  if (!pricing) return [];

  const modelObj = pricing.models.find((m) => m.name === modelName);
  return modelObj?.pricing_rules || [];
}

/**
 * Gets a specific pricing rule by name for a model
 * @param modelName - The name of the model
 * @param ruleName - The name of the pricing rule (e.g., "input_token", "output_token")
 * @param pricing - Optional pricing data (if not provided, will use query)
 * @returns The pricing rule if found, undefined otherwise
 */
export function getModelPricingRule(
  modelName: string,
  ruleName: RuleName,
  pricing?: Pricing
): PricingRule | undefined {
  const rules = getModelPricingRules(modelName, pricing);
  return rules.find((r) => r.name === ruleName);
}

/**
 * Whether a model's cache tokens are billed separately (passthrough deck) rather
 * than absorbed into input/output pricing (absorb deck). Absorb decks have their
 * cache_read_token/cache_write_token rules stripped from the pricing response
 * server-side, so their presence in the model's rules is the only signal.
 * @param modelName - The name of the model
 * @param pricing - Pricing data to look up the model's rules in
 * @returns True if the model has a cache_read_token or cache_write_token rule
 */
export function billsCacheSeparately(
  modelName: string | undefined,
  pricing: Pricing | undefined
): boolean {
  if (!modelName || !pricing) return false;

  const rules = getModelPricingRules(modelName, pricing);
  return rules.some((r) => r.name === "cache_read_token" || r.name === "cache_write_token");
}

/**
 * Whether the UI shows a model's cached tokens. Absorb decks hide them (the
 * cache discount is folded into the margin), but a BYOK model is paid straight
 * to the provider with the org's own key: there is no margin to hide, and those
 * models (e.g. OpenRouter) have no deck pricing rules at all.
 * @param model - The model, with its BYOK status from the models listing
 * @param pricing - Organization pricing data
 */
export function showsCachedTokens(
  model: Pick<Model, "name" | "byok"> | undefined,
  pricing: Pricing | undefined
): boolean {
  if (!model) return false;
  return model.byok?.configured === true || billsCacheSeparately(model.name, pricing);
}

/**
 * Per-row equivalent of `showsCachedTokens` for a usage item: a BYOK item is
 * paid straight to the provider (no margin to hide), and a platform item shows
 * cache only when its model bills cache separately. Platform rows on an absorb
 * deck stay hidden - the share reveals the discount as much as the absolute
 * count.
 * @param item - The usage item, with its cost attribution and identifier
 * @param pricing - Organization pricing data
 */
export function showsItemCache(
  item: Pick<UsageRequestItem, "cost_attribution" | "identifier">,
  pricing: Pricing | undefined
): boolean {
  return item.cost_attribution === "client_byok"
    || billsCacheSeparately(item.identifier, pricing);
}

/**
 * Cache read share of the whole prompt (`quantity`/input already includes the
 * cache reads, OpenAI convention), formatted as a locale-aware percentage with
 * no decimals. Returns null when the provider did not report cache reads -
 * never synthesize 0%. A reported 0 is a real 0%.
 * @param cacheReadTokens - Reported cache read tokens, null/undefined when not reported
 * @param inputTokens - The whole prompt (cache included)
 */
export function formatCacheShare(
  cacheReadTokens: number | null | undefined,
  inputTokens: number | null | undefined
): string | null {
  if (cacheReadTokens == null || !inputTokens || inputTokens <= 0) return null;

  return (cacheReadTokens / inputTokens).toLocaleString("default", {
    style: "percent",
    maximumFractionDigits: 0,
  });
}

/**
 * Pricing rules that make up the prompt of one request+model group. A
 * passthrough deck may split it into `input_token` (net) + `cache_read_token` +
 * `cache_write_token`; every other deck has a single `input_token` row with the
 * whole prompt and the cache columns on it.
 */
const CACHE_PROMPT_RULES = new Set<string>([
  "input_token",
  "cache_read_token",
  "cache_write_token",
]);

export interface CacheTokenTotals {
  /** Whole prompt: net input + cache read + cache write quantities. */
  prompt: number;
  /** Sum of reported cache reads over the prompt rows, null when none reported. */
  read: number | null;
  /** Sum of reported cache writes over the prompt rows, null when none reported. */
  write: number | null;
}

/**
 * Aggregate the cache columns of one request+model group. Never divide or
 * subtract against a single row's quantity: with a split passthrough row, the
 * net input alone is much smaller than the prompt. Rows that did not report a
 * column do not contribute to it, so a group with no report keeps null.
 */
export function summarizeCacheTokens(
  rows: Array<
    Pick<UsageRequestItem, "pricing_rule" | "quantity" | "cache_read_tokens" | "cache_write_tokens">
  >
): CacheTokenTotals {
  let prompt = 0;
  let read = 0;
  let write = 0;
  let readReported = false;
  let writeReported = false;

  for (const row of rows) {
    if (!CACHE_PROMPT_RULES.has(row.pricing_rule)) continue;

    prompt += row.quantity;

    if (row.cache_read_tokens != null) {
      read += row.cache_read_tokens;
      readReported = true;
    }
    if (row.cache_write_tokens != null) {
      write += row.cache_write_tokens;
      writeReported = true;
    }
  }

  return {
    prompt,
    read: readReported ? read : null,
    write: writeReported ? write : null,
  };
}

/**
 * Cache read share of a group, treating an unreported read as 0 when a write
 * was reported (same accounting the backend uses). Null when neither was
 * reported.
 */
export function formatCacheTotals(totals: CacheTokenTotals): string | null {
  const read = totals.read ?? (totals.write != null ? 0 : null);
  return formatCacheShare(read, totals.prompt);
}

/**
 * Cache display for a usage-table group: the share to render as its own small
 * element next to the quantity, plus the tooltip body (label, read/prompt
 * detail and, when reported, the write count). Null when nothing was reported,
 * so callers render nothing.
 */
export function formatCacheHit(
  totals: CacheTokenTotals
): { share: string; tooltip: string } | null {
  const share = formatCacheTotals(totals);
  if (!share) return null;

  let tooltip = `${$t("usage_table.cache_hit")}: ${$t("usage_table.cache_detail", {
    cached: (totals.read ?? 0).toLocaleString(),
    input: totals.prompt.toLocaleString(),
  })}`;

  if (totals.write != null) {
    tooltip += ` · ${$t("usage_table.cache_write_detail", {
      written: totals.write.toLocaleString(),
    })}`;
  }

  return { share, tooltip };
}

/**
 * Whether a usage row may show the `cached` badge. Same visibility rule as the
 * counts: absorb-deck platform rows hide it, BYOK and passthrough decks show it.
 */
export function shouldShowCachedBadge(
  item: Pick<UsageRequestItem, "cost_attribution" | "identifier" | "is_cached">,
  pricing: Pricing | undefined
): boolean {
  return item.is_cached === true && showsItemCache(item, pricing);
}

/**
 * Calculates the cost for a given amount of units based on a pricing rule
 * @param tokens - Number of units (tokens, characters, seconds, etc.)
 * @param rule - The pricing rule to use for calculation
 * @returns The calculated cost
 */
export function calculateTokenCost(tokens: number, rule: PricingRule): number {
  if (!rule) return 0;

  // Only support token and character metrics for now
  if (
    rule.metric.type !== "tokens" &&
    rule.metric.type !== "characters" &&
    rule.metric.type !== "time"
  ) {
    return 0;
  }

  const price = rule.selling_price;

  // Calculate based on metric.per
  const per = rule.metric.per || "unit";
  if (per === "million") {
    return (price * tokens) / 1_000_000;
  } else if (per === "thousand") {
    return (price * tokens) / 1_000;
  } else {
    // per === "unit"
    return price * tokens;
  }
}

/**
 * Calculates the estimated cost for input and output tokens
 * @param inputTokens - Number of input tokens
 * @param outputTokens - Number of output tokens
 * @param modelName - Name of the model
 * @param pricing - Optional pricing data (if not provided, will use query)
 * @returns Object with inputCost, outputCost, and total
 */
export function calculateMessageCost(
  inputTokens: number,
  outputTokens: number,
  modelName: string,
  pricing?: Pricing
): {
  inputCost: number;
  outputCost: number;
  total: number;
} {
  if (!pricing) {
    const pricingQuery = useOrganizationPricingQuery();
    pricing = pricingQuery.data.value ?? undefined;
  }

  if (!pricing) {
    return { inputCost: 0, outputCost: 0, total: 0 };
  }

  // Find the model
  const modelObj = pricing.models.find((m) => m.name === modelName);
  if (!modelObj) {
    return { inputCost: 0, outputCost: 0, total: 0 };
  }

  // Calculate model costs
  let inputCost = 0;
  let outputCost = 0;
  if (modelObj.pricing_rules) {
    const inputRule = modelObj.pricing_rules.find((r) => r.name === "input_token");
    const outputRule = modelObj.pricing_rules.find((r) => r.name === "output_token");

    if (inputRule) {
      inputCost = calculateTokenCost(inputTokens, inputRule);
    }
    if (outputRule) {
      outputCost = calculateTokenCost(outputTokens, outputRule);
    }
  }

  // Calculate agent service costs (if applicable)
  let agentInputCost = 0;
  let agentOutputCost = 0;
  const agentService = pricing.services?.find((s) => s.service_name === "agent");
  if (agentService?.pricing_rules) {
    const parsedAgentRules = agentService.pricing_rules.flatMap((rule) => {
      const parsed = pricingRuleSchema.safeParse(rule);
      return parsed.success ? [parsed.data] : [];
    });
    const agentInputRule = parsedAgentRules.find((r) => r.name === "input_token");
    const agentOutputRule = parsedAgentRules.find((r) => r.name === "output_token");

    if (agentInputRule) {
      agentInputCost = calculateTokenCost(inputTokens, agentInputRule);
    }
    if (agentOutputRule) {
      agentOutputCost = calculateTokenCost(outputTokens, agentOutputRule);
    }
  }

  const totalInputCost = inputCost + agentInputCost;
  const totalOutputCost = outputCost + agentOutputCost;

  return {
    inputCost: totalInputCost,
    outputCost: totalOutputCost,
    total: totalInputCost + totalOutputCost,
  };
}

/**
 * Formats a price with currency
 * @param value - The price value
 * @param currency - The currency code (e.g., "USD")
 * @returns Formatted price string
 */
function formatPriceWithCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("default", {
    style: "currency",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: currency,
  }).format(value);
}

/**
 * Formats the complete price information for a model
 * @param modelName - The name of the model
 * @param currency - The currency code
 * @param pricing - Optional pricing data (if not provided, will use query)
 * @returns Formatted price string or "Free" if no pricing
 */
export function formatModelPrice(modelName: string, currency: string, pricing?: Pricing): string {
  const rules = getModelPricingRules(modelName, pricing);

  if (!rules.length) return "";

  const prices = [] as string[];

  // Look for input/output token rules
  const inputRule = rules.find((r) => r.name === "input_token");
  const outputRule = rules.find((r) => r.name === "output_token");

  if (inputRule) {
    const unitLabel = getUnitLabel(inputRule);
    prices.push(
      `Input tokens ${formatPriceWithCurrency(inputRule.selling_price, currency)} ${unitLabel}`
    );
  }

  if (outputRule) {
    const unitLabel = getUnitLabel(outputRule);
    prices.push(
      `Output ${formatPriceWithCurrency(outputRule.selling_price, currency)} ${unitLabel}`
    );
  }

  // Look for other common rules
  const ttsRule = rules.find(isTtsPricingRule);
  const transcriptionRule = rules.find((r) => r.name === "transcription");
  const embeddingRule = rules.find((r) => r.name === "embedding_token");

  if (ttsRule) {
    const unitLabel = getUnitLabel(ttsRule);
    prices.push(formatPriceWithCurrency(ttsRule.selling_price, currency) + " " + unitLabel);
  }

  if (transcriptionRule) {
    const unitLabel = getUnitLabel(transcriptionRule);
    prices.push(
      formatPriceWithCurrency(transcriptionRule.selling_price, currency) + " " + unitLabel
    );
  }

  if (embeddingRule) {
    const unitLabel = getUnitLabel(embeddingRule);
    prices.push(formatPriceWithCurrency(embeddingRule.selling_price, currency) + " " + unitLabel);
  }

  return prices.join(" / ") || $t("free");
}

/**
 * Gets the unit label for a pricing rule based on its metric
 * @param rule - The pricing rule
 * @returns Formatted unit label (e.g., "per 1M tokens")
 */
function getUnitLabel(rule: PricingRule): string {
  const { metric } = rule;

  if (metric.per) {
    const perKey = metric.per; // "million", "thousand", "unit"
    const unitKey = metric.unit; // "token", "character", "second"

    // Try specific key first: per_million_tokens
    const specificKey = `per_${perKey}_${unitKey}s`;
    const specificTranslation = $t(`pricing:units.${specificKey}` as any);
    if (specificTranslation && specificTranslation !== specificKey) {
      return specificTranslation as string;
    }

    // Try singular: per_million_token
    const singularKey = `per_${perKey}_${unitKey}`;
    const singularTranslation = $t(`pricing:units.${singularKey}` as any);
    if (singularTranslation && singularTranslation !== singularKey) {
      return singularTranslation as string;
    }
  }

  // Fallback to unit key
  return $t(`pricing:units.${metric.unit}` as any) as string;
}

/**
 * Blended per-1M-token price (org currency) for every model that has one, keyed
 * by model name. Feeds the LLM picker's Cost star (see `PriceByModel` in
 * `pages/agents/model-ratings.ts`). The blend weights input higher than output
 * (prompts dominate); a model priced on only one side uses that side alone.
 * Uses `calculateTokenCost(1M)` so `metric.per` (million/thousand/unit) is
 * normalised the same way the rest of the pricing math is.
 */
export function blendedPriceByModel(pricing?: Pricing): Map<string, number> {
  const map = new Map<string, number>();
  if (!pricing) return map;

  for (const model of pricing.models) {
    const inputRule = model.pricing_rules.find((r) => r.name === "input_token");
    const outputRule = model.pricing_rules.find((r) => r.name === "output_token");

    const inputPer1M = inputRule ? calculateTokenCost(1_000_000, inputRule) : null;
    const outputPer1M = outputRule ? calculateTokenCost(1_000_000, outputRule) : null;

    let blended: number | null = null;
    if (inputPer1M != null && outputPer1M != null) {
      blended = inputPer1M * PRICE_BLEND_WEIGHTS.input + outputPer1M * PRICE_BLEND_WEIGHTS.output;
    } else {
      blended = inputPer1M ?? outputPer1M;
    }

    if (blended != null && blended > 0) map.set(model.name, blended);
  }

  return map;
}

/**
 * Gets complete pricing information for a model
 * @param modelName - The name of the model
 * @param pricing - Optional pricing data (if not provided, will use query)
 * @returns Object with pricing rules and helper methods
 */
export function getModelPricingInfo(modelName: string, pricing?: Pricing) {
  const rules = getModelPricingRules(modelName, pricing);

  return {
    rules,
    hasInputRule: rules.some((r) => r.name === "input_token"),
    hasOutputRule: rules.some((r) => r.name === "output_token"),
    hasTTSRule: rules.some(isTtsPricingRule),
    hasTranscriptionRule: rules.some((r) => r.name === "transcription"),
    hasEmbeddingRule: rules.some((r) => r.name === "embedding_token"),
    isFree: rules.length === 0,
  };
}
